import { useEffect, useState } from "react";
import {
  Empty,
  Button,
  message,
  Tooltip,
  Pagination,
  Spin,
  Typography,
  List,
  Space,
  Tag,
  Input,
  Select,
  Dropdown,
  Menu,
  Divider,
  Skeleton,
  Segmented,
  Avatar,
  Col,
  Row,
  Checkbox,
  Progress,
  Modal,
} from "antd";
import {
  FilePdfOutlined,
  EyeOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileTextOutlined,
  FileImageOutlined,
  FileUnknownOutlined,
  SearchOutlined,
  FilterOutlined,
  ShareAltOutlined,
  StarOutlined,
  StarFilled,
  SortAscendingOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  CloudUploadOutlined,
  MoreOutlined,
  CalendarOutlined,
  UserOutlined,
  FileOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import httpBase from "../../../../utils/http.utils";
import "./drawer.css";
import { FILE_SETUP_DELETE_FILE } from "../../../../constants/url";
import PdfViewer from "../../../../components/pdf-viewer";
const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const ExistingFiles = ({
  existingFiles,
  page,
  loading,
  limit,
  totalFiles,
  setPage,
  fetchExistingFiles,
  fetchSources,
  fetchTOC,
  setActiveTab,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState("");
  const [previewPdfTitle, setPreviewPdfTitle] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState("");
  const handlePreview = async (file) => {
    console.log("file", file);
    try {
      const response = await httpBase().get(`/file_setup/get_doc/Files/${file}`, {
        responseType: "json",
      });
      if (response.data && response.data.presigned_url) {
        const extension = getFileExtension(file);
        if (extension === "PDF") {
          setPreviewPdfUrl(response?.data?.presigned_url);
          setPreviewPdfTitle(file);
          setIsPdfViewerOpen(true);
        } else {
          window.open(response?.data?.presigned_url, "_blank");
        }
      } else {
        console.error("Presigned URL not found in the response.");
      }
    } catch (error) {
      console.error("Error fetching file:", error);
      message.error("Failed to open file.");
    }
  };

  const handleDelete = async (fileName) => {
    try {
      const response = await httpBase().delete(`${FILE_SETUP_DELETE_FILE}/${fileName}`);
      if (response.status === 200) {
        message.success("File deleted successfully");
        setSelectedFiles(selectedFiles.filter((name) => name !== fileName));
        fetchExistingFiles(page);
        fetchSources();
        fetchTOC();
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      message.error("Failed to delete file");
    }
  };

  const openDeleteDialog = (e, fileName) => {
    e.stopPropagation();
    setFileToDelete(fileName);
    setDeleteDialogOpen(true);
  };

  const handleBulkDelete = async () => {
    Modal.confirm({
      title: `Delete ${selectedFiles.length} file${
        selectedFiles.length > 1 ? "s" : ""
      }?`,
      icon: <ExclamationCircleFilled />,
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        let successCount = 0;
        let failCount = 0;

        for (const fileName of selectedFiles) {
          try {
            const response = await httpBase().delete(
              `${FILE_SETUP_DELETE_FILE}/${fileName}`
            );
            if (response.status === 200) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          message.success(
            `${successCount} file${
              successCount > 1 ? "s" : ""
            } deleted successfully`
          );
          setSelectedFiles([]);
          fetchExistingFiles(page);
        }

        if (failCount > 0) {
          message.error(
            `Failed to delete ${failCount} file${failCount > 1 ? "s" : ""}`
          );
        }
      },
    });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelectedFiles([]);
  };

  const toggleFileSelection = (fileName) => {
    if (selectedFiles.includes(fileName)) {
      setSelectedFiles(selectedFiles.filter((name) => name !== fileName));
    } else {
      setSelectedFiles([...selectedFiles, fileName]);
    }
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      const allFileNames = existingFiles.map((file) => file.name || file);
      setSelectedFiles(allFileNames);
    } else {
      setSelectedFiles([]);
    }
  };

  // Get file extension
  const getFileExtension = (filename) => {
    return filename
      .slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2)
      .toUpperCase();
  };

  // Get appropriate icon based on file extension
  const getFileIcon = (extension) => {
    switch (extension) {
      case "PDF":
        return <FilePdfOutlined style={{ fontSize: 24, color: "#e74c3c" }} />;
      case "DOC":
      case "DOCX":
        return <FileWordOutlined style={{ fontSize: 24, color: "#3498db" }} />;
      case "XLS":
      case "XLSX":
        return <FileExcelOutlined style={{ fontSize: 24, color: "#2ecc71" }} />;
      case "TXT":
      case "CSV":
        return <FileTextOutlined style={{ fontSize: 24, color: "#f39c12" }} />;
      case "JPG":
      case "JPEG":
      case "PNG":
      case "GIF":
        return <FileImageOutlined style={{ fontSize: 24, color: "#9b59b6" }} />;
      default:
        return (
          <FileUnknownOutlined style={{ fontSize: 24, color: "#95a5a6" }} />
        );
    }
  };

  // Render different views
  const renderListView = () => (
    <List
      className="file-list"
      itemLayout="horizontal"
      dataSource={existingFiles}
      renderItem={(file) => {
        const extension = getFileExtension(file.name || file);
        const fileName = file.name || file;
        const isSelected = selectedFiles.includes(fileName);

        return (
          <List.Item
            key={fileName}
            className={isSelected ? "selected-file-item" : ""}
            onClick={() => {
              if (extension === "PDF") {
                handlePreview(fileName);
              }
            }}
            style={{
              transition: "all 0.3s ease",
              backgroundColor: isSelected ? " #f0f5ff" : "transparent",
              borderLeft: isSelected
                ? "3px solid #1890ff"
                : "3px solid transparent",
              padding: "12px 0",
              borderRadius: "8px",
              marginBottom: "8px",
              overflow: "hidden",
              cursor: extension === "PDF" ? "pointer" : "default",
            }}
          >
            <div
              className="file-item-layout"
            >
              {/* Column 1: Checkbox, Icon, and File Name */}
              <div
                className="file-info-column"
              >
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleFileSelection(fileName)}
                  style={{ marginLeft: 13, marginRight: 12, flexShrink: 0 }}
                />
                <Avatar
                  shape="square"
                  size={40}
                  icon={getFileIcon(extension)}
                  style={{
                    background: "white",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    marginRight: 12,
                    flexShrink: 0
                  }}
                />
                <Tooltip title={fileName} placement="left">
                  <Text
                    strong
                    onClick={() => {
                      if (extension === "PDF") {
                        handlePreview(fileName);
                      }
                    }}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                      flex: 1,
                      minWidth: 0,
                      cursor: extension === "PDF" ? "pointer" : "default",
                    }}
                  >
                    {fileName}
                  </Text>
                </Tooltip>
              </div>

              {/* Column 2: Control Buttons */}
              <div
                className="file-actions-column"
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Preview">
                  <Button
                    icon={
                      <EyeOutlined style={{ color: "rgb(93, 132, 221)" }} />
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreview(fileName);
                    }}
                    type="text"
                    size="small"
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                  />
                </Tooltip>
                <Tooltip title="Delete">
                  <Button
                    icon={<DeleteOutlined style={{ color: "red" }} />}
                    type="text"
                    size="small"
                    onClick={(e) => openDeleteDialog(e, fileName)}
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", marginRight: 13 }}
                  />
                </Tooltip>
              </div>
            </div>
          </List.Item>
        );
      }}
    />
  );

  // Custom skeleton loaders
  const renderSkeletonLoaders = () =>
    Array(6)
      .fill()
      .map((_, index) => (
        <Skeleton
          key={index}
          active
          avatar={{ shape: "square", size: 40 }}
          paragraph={{ rows: 1, width: ["80%"] }}
          style={{ margin: "16px 0" }}
        />
      ));

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {selectedFiles.length > 0 && (
        <div
          style={{
            marginBottom: 10,
            padding: "8px 16px",
            background: "#f0f5ff",
            borderRadius: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Space>
            <Checkbox
              onChange={toggleSelectAll}
              checked={selectedFiles.length === existingFiles.length}
              indeterminate={
                selectedFiles.length > 0 &&
                selectedFiles.length < existingFiles.length
              }
            />
            <Text>
              {selectedFiles.length} of {existingFiles.length} selected
            </Text>
          </Space>
          <Space>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={handleBulkDelete}
            >
              Delete
            </Button>
          </Space>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {loading ? (
          renderSkeletonLoaders()
        ) : existingFiles && existingFiles.length > 0 ? (
          <>{renderListView()}</>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Text style={{ fontSize: 16, marginBottom: 8 }}>
                  No files found
                </Text>
              </div>
            }
          ></Empty>
        )}
      </div>

      <Modal
        open={isPdfViewerOpen}
        title={previewPdfTitle || "PDF Preview"}
        footer={null}
        onCancel={() => {
          setIsPdfViewerOpen(false);
          setPreviewPdfUrl("");
          setPreviewPdfTitle("");
        }}
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{ height: "80vh", padding: 0 }}
        destroyOnClose
      >
        {previewPdfUrl ? (
          <PdfViewer
            pdfUrls={[{ url: previewPdfUrl, title: previewPdfTitle || "PDF Preview" }]}
            zoomable
            rotatable
            fullscreenable
            downloadable
            showDocumentNav={false}
            noBorder
          />
        ) : null}
      </Modal>

      <Modal
        open={deleteDialogOpen}
        title="Delete file"
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        onCancel={() => {
          setDeleteDialogOpen(false);
          setFileToDelete("");
        }}
        onOk={async () => {
          if (fileToDelete) {
            await handleDelete(fileToDelete);
          }
          setDeleteDialogOpen(false);
          setFileToDelete("");
        }}
      >
        <Text>
          Delete the knowledge data related to{" "}
          <Text strong>{fileToDelete}</Text>?
        </Text>
      </Modal>

      <style jsx global>{`
        .file-list .ant-list-item {
          cursor: pointer;
        }
        .file-list .ant-list-item:hover {
          background-color: #fafafa;
        }
        .selected-file-item {
          border-left: 3px solid #1890ff;
        }
        .file-list .ant-list-item .ant-space-item {
          display: flex;
          align-items: center;
        }
      `}</style>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-end",
          padding: "15px 0",
          borderTop: "1px solid #f0f0f0",
          background: "white",
          width: "100%",
        }}
      >
        <Pagination
          current={page}
          pageSize={limit}
          total={totalFiles}
          onChange={handlePageChange}
          showSizeChanger={false}
          showQuickJumper={false}
        />
      </div>
    </div>
  );
};

export default ExistingFiles;
